<template>
  <div>
    {{ /*edit commission after booking modal (supermanager only)*/ }}
    <b-modal
      v-model="showModal"
      :title="'Edit Commission — ' + (trade ? trade.TcUti : '')"
      size="xl"
      :body-bg-variant="'light'"
      :header-bg-variant="'dark'"
      body-class="compact_form_modal"
      footer-class="compact_modal_footer"
      no-close-on-backdrop
      @hidden="resetModal"
    >
      <b-overlay :show="loading" rounded="sm" no-fade>
        <b-container>
          <b-alert show variant="warning" class="p-1 mt-1">
            This trade is already booked. Changing a commission amount
            recalculates that side's rate and cash amount and the TC profit,
            and updates the related journal entries in place. A side whose
            commission is left unchanged is not touched. A history copy of the
            trade and of every changed journal line is kept.
          </b-alert>

          <b-row v-if="currentTrade" class="mb-1">
            <b-col cols="12">
              <strong>{{ productType }}</strong> |
              <strong>{{ currentTrade.buySell }}</strong>
              {{ formatNumber(currentTrade.quantity, 0) }}
              {{ currentTrade.securityDescription }} |
              {{ currentTrade.ccy }} | TC Profit:
              <strong>{{ formatNumber(current.tcProfit, 2) }}</strong>
            </b-col>
          </b-row>

          <b-row v-if="currentTrade" class="align-items-end">
            <b-col cols="12" md="5">
              <b-form-group label-for="newCommissionAmount">
                <template #label>
                  Client Commission ({{ currentTrade.ccy }}) — {{ currentTrade.client }} ({{ currentTrade.account }})
                </template>
                <b-form-input
                  id="newCommissionAmount"
                  v-model="newCommissionAmount"
                  type="number"
                  step="0.01"
                  min="0"
                  size="sm"
                  @input="clearPreview"
                />
                <small class="text-muted">
                  Current: {{ formatNumber(current.commissionAmount, 2) }} |
                  rate {{ formatNumber(current.commissionRate, 8) }} bps
                </small>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="5">
              <b-form-group label-for="newCounterpartyCommission">
                <template #label>
                  Counterparty Commission ({{ currentTrade.ccy }}) — {{ currentTrade.counterparty }} ({{ currentTrade.counterpartyAccount }})
                </template>
                <b-form-input
                  id="newCounterpartyCommission"
                  v-model="newCounterpartyCommission"
                  type="number"
                  step="0.01"
                  min="0"
                  size="sm"
                  @input="clearPreview"
                />
                <small class="text-muted">
                  Current: {{ formatNumber(current.counterpartyCommission, 2) }} |
                  rate {{ formatNumber(current.counterpartyRate, 8) }} bps
                </small>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="2">
              <b-form-group>
                <b-button
                  variant="primary"
                  size="sm"
                  :disabled="!amountsAreValid || loading"
                  @click="runPreview"
                >
                  Preview Changes
                </b-button>
              </b-form-group>
            </b-col>
          </b-row>

          <template v-if="preview && !preview.unchanged">
            <b-alert
              v-for="(warning, index) in preview.warnings"
              :key="'warning-' + index"
              show
              variant="warning"
              class="p-1 mt-1"
            >
              {{ warning }}
            </b-alert>

            <h6 class="mt-1">Trade fields that will be updated</h6>
            <b-table
              small
              striped
              responsive
              head-variant="light"
              class="compact_table"
              :items="preview.tradeChanges"
              :fields="tradeFields"
            >
              <template #cell(field)="data">
                <strong>{{ tradeFieldLabels[data.item.field] }}</strong>
              </template>
              <template #cell(old)="data">
                {{ formatNumber(data.item.old, fieldDecimals(data.item.field)) }}
              </template>
              <template #cell(new)="data">
                <strong :class="changedClass(data.item.old, data.item.new)">
                  {{ formatNumber(data.item.new, fieldDecimals(data.item.field)) }}
                </strong>
              </template>
            </b-table>

            <h6 class="mt-1">
              Journal entries that will be updated ({{ preview.journalChanges.length }})
            </h6>
            <b-alert
              v-if="!preview.journalCreated"
              show
              variant="info"
              class="p-1"
            >
              No journal entries exist for this trade yet. Only the trade will
              be updated.
            </b-alert>
            <b-table
              v-else
              small
              striped
              responsive
              head-variant="light"
              class="compact_table"
              :items="preview.journalChanges"
              :fields="journalFields"
              show-empty
              empty-text="No journal entries need to change."
            >
              <template #cell(action)="data">
                <b-badge :variant="data.item.action === 'insert' ? 'success' : 'warning'">
                  {{ data.item.action === "insert" ? "NEW" : "UPDATE" }}
                </b-badge>
                <b-badge v-if="data.item.dvp" variant="info" class="ml-25">DVP</b-badge>
              </template>
              <template #cell(side)="data">
                {{ sideLabels[data.item.side] }}
              </template>
              <template #cell(tcUti)="data">
                {{ data.item.tcUti || "(new)" }}
              </template>
              <template #cell(account)="data">
                <strong>{{ data.item.accountName }}</strong><br />
                <small>{{ data.item.accountNumber }} / {{ data.item.accountCode }}</small>
              </template>
              <template #cell(amount)="data">
                <span v-if="data.item.oldAmount !== null">
                  {{ formatNumber(data.item.oldAmount, 2) }} →
                </span>
                <strong :class="changedClass(data.item.oldAmount, data.item.newAmount)">
                  {{ formatNumber(data.item.newAmount, 2) }}
                </strong>
              </template>
              <template #cell(description)="data">
                <template v-if="data.item.oldDescription !== data.item.newDescription">
                  <span v-if="data.item.oldDescription">
                    <del>{{ data.item.oldDescription }}</del> →
                  </span>
                  <strong class="text-danger">{{ data.item.newDescription }}</strong>
                </template>
                <span v-else>{{ data.item.newDescription }}</span>
              </template>
            </b-table>

            <b-form-group label="Reason (required)" label-for="commissionReason" class="mt-1">
              <b-form-textarea
                id="commissionReason"
                v-model="reason"
                rows="2"
                max-rows="4"
                size="sm"
              />
            </b-form-group>
          </template>

          <b-alert v-if="preview && preview.unchanged" show variant="info" class="p-1">
            Neither commission differs from the current one. Nothing to update.
          </b-alert>
        </b-container>
      </b-overlay>

      <template #modal-footer>
        <b-button size="sm" variant="secondary" @click="showModal = false">
          Cancel
        </b-button>
        <b-button
          size="sm"
          variant="danger"
          :disabled="!canApply || loading"
          @click="askConfirmation"
        >
          Apply Commission Change
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  props: {
    // "Equity" | "Bond"
    productType: { type: String, required: true },
  },

  data() {
    return {
      showModal: false,
      loading: false,
      trade: null,
      currentTrade: null,
      current: {},
      newCommissionAmount: null,
      newCounterpartyCommission: null,
      preview: null,
      reason: "",
      tradeFieldLabels: {
        CommissionRate: "Client Rate (Bps)",
        CommissionAmount: "Client Commission",
        ClientAmount: "Client Amount",
        CounterpartyRate: "Counterparty Rate (Bps)",
        CounterpartyCommission: "Counterparty Commission",
        CounterpartyAmount: "Counterparty Amount",
        TcProfit: "TC Profit",
      },
      sideLabels: {
        client: "Client",
        cpty: "Counterparty",
        profit: "P&L",
      },
      tradeFields: [
        { key: "field", label: "Field" },
        { key: "old", label: "Current Value" },
        { key: "new", label: "New Value" },
      ],
      journalFields: [
        { key: "action", label: "" },
        { key: "side", label: "Side" },
        { key: "tcUti", label: "Journal Ref" },
        { key: "account", label: "Account" },
        { key: "underlying", label: "Underlying" },
        { key: "transactionType", label: "Transaction Type" },
        { key: "amount", label: "Amount" },
        { key: "description", label: "Entry Description" },
      ],
    };
  },

  computed: {
    isSupermanager() {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      return userData.role === "supermanager";
    },
    amountsAreValid() {
      return [this.newCommissionAmount, this.newCounterpartyCommission].every(
        (amount) =>
          amount !== null &&
          amount !== "" &&
          !isNaN(Number(amount)) &&
          Number(amount) >= 0
      );
    },
    canApply() {
      return (
        this.preview &&
        !this.preview.unchanged &&
        this.reason.trim() !== ""
      );
    },
  },

  methods: {
    // called by the parent list with the selected row
    open(trade) {
      if (!this.isSupermanager) {
        this.toast("Warning", "Only supermanagers can change commission after booking.", "warning");
        return;
      }
      this.resetModal();
      this.trade = trade;
      this.showModal = true;
      this.loadCurrent();
    },

    resetModal() {
      this.trade = null;
      this.currentTrade = null;
      this.current = {};
      this.newCommissionAmount = null;
      this.newCounterpartyCommission = null;
      this.preview = null;
      this.reason = "";
      this.loading = false;
    },

    clearPreview() {
      this.preview = null;
    },

    previewRequest(clientCommission, counterpartyCommission) {
      return store.dispatch("equity&bond/previewTradeCommissionUpdate", {
        productType: this.productType,
        tradeId: this.trade.ID,
        newCommissionAmount: clientCommission,
        newCounterpartyCommission: counterpartyCommission,
      });
    },

    // preview without commissions just to read the current values from the server
    loadCurrent() {
      this.loading = true;
      this.previewRequest(null, null)
        .then((res) => {
          if (res.data.info !== "Pass") {
            this.toast("Warning", res.data.message, "warning");
            this.showModal = false;
            return;
          }
          this.currentTrade = res.data.trade;
          this.current = res.data.current;
          this.newCommissionAmount = this.current.commissionAmount;
          this.newCounterpartyCommission = this.current.counterpartyCommission;
        })
        .catch((error) => {
          console.log(error);
          this.toast("Error", "Could not load the trade.", "danger");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    runPreview() {
      this.loading = true;
      this.preview = null;
      this.previewRequest(this.newCommissionAmount, this.newCounterpartyCommission)
        .then((res) => {
          if (res.data.info === "Pass") {
            this.preview = res.data;
          } else {
            this.toast("Warning", res.data.message, "warning");
          }
        })
        .catch((error) => {
          console.log(error);
          this.toast("Error", "Preview failed.", "danger");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    askConfirmation() {
      const updates = this.preview.journalChanges.filter((j) => j.action === "update").length;
      const inserts = this.preview.journalChanges.filter((j) => j.action === "insert").length;
      const lines = this.preview.tradeChanges
        .map((c) => {
          const decimals = this.fieldDecimals(c.field);
          return `${this.tradeFieldLabels[c.field]}: ${this.formatNumber(c.old, decimals)} → <b>${this.formatNumber(c.new, decimals)}</b>`;
        })
        .join("<br/>");

      this.$swal({
        title: "Apply commission change?",
        html:
          `<div style="text-align:left">` +
          `<p><b>${this.trade.TcUti}</b></p>` +
          `<p>${lines}</p>` +
          `<p>1 trade, ${updates} journal line(s) updated` +
          (inserts > 0 ? `, ${inserts} journal line(s) inserted` : "") +
          `.</p></div>`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, apply",
        cancelButtonText: "Cancel",
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-outline-secondary ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.applyChange();
        }
      });
    },

    applyChange() {
      this.loading = true;
      store
        .dispatch("equity&bond/confirmTradeCommissionUpdate", {
          productType: this.productType,
          tradeId: this.trade.ID,
          newCommissionAmount: this.newCommissionAmount,
          newCounterpartyCommission: this.newCounterpartyCommission,
          reason: this.reason,
          previewCurrent: this.preview.current,
        })
        .then((res) => {
          if (res.data.info === "Pass") {
            this.toast("Success", res.data.message, "success");
            this.showModal = false;
            this.$emit("updated", true);
          } else {
            this.toast("Warning", res.data.message, "warning");
            if (res.data.info === "stale") {
              this.preview = null;
            }
          }
        })
        .catch((error) => {
          console.log(error);
          this.toast("Error", "Commission update failed.", "danger");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    formatNumber(value, decimals) {
      if (value === null || value === undefined || value === "") return "-";
      return Number(value).toLocaleString("en-US", {
        minimumFractionDigits: decimals === 8 ? 0 : decimals,
        maximumFractionDigits: decimals,
      });
    },

    fieldDecimals(field) {
      return field === "CommissionRate" || field === "CounterpartyRate" ? 8 : 2;
    },

    changedClass(oldValue, newValue) {
      return oldValue === null || Number(oldValue) !== Number(newValue)
        ? "text-danger"
        : "";
    },

    toast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          text,
          icon: variant === "success" ? "CheckIcon" : "AlertTriangleIcon",
          variant,
        },
      });
    },
  },
};
</script>
